import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Container,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CodeIcon from '@mui/icons-material/Code';
import LanguageIcon from '@mui/icons-material/Language';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isCode?: boolean;
  pdfData?: string;
}

interface ChatProps {
  onLogout: () => void;
}

const Chat: React.FC<ChatProps> = ({ onLogout }) => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: t('chat.welcome'),
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // تمرير إلى آخر الرسائل عند إضافة رسالة جديدة
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (input.trim() === '' && !pdfFile) return;

    // إضافة رسالة المستخدم
    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
      pdfData: pdfFile ? URL.createObjectURL(pdfFile) : undefined
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setPdfFile(null);
    setIsLoading(true);

    // محاكاة استجابة الذكاء الاصطناعي (سيتم استبدالها بالاتصال الفعلي بـ OpenRouter)
    setTimeout(() => {
      const isCode = input.includes('code') || input.includes('برمجة') || input.includes('كود');
      
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: isCode 
          ? `\`\`\`javascript\n// مثال على كود JavaScript\nfunction sayHello(name) {\n  return "مرحباً، " + name + "!";\n}\n\nconsole.log(sayHello("مستخدم"));\n\`\`\``
          : `هذه استجابة تجريبية للذكاء الاصطناعي. سيتم استبدالها بالاستجابة الفعلية من DeepSeek عبر OpenRouter. ردك على: "${input}"`,
        sender: 'ai',
        timestamp: new Date(),
        isCode: isCode
      };
      
      setMessages(prev => [...prev, aiResponse]);
      setIsLoading(false);
    }, 1500);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPdfFile(e.target.files[0]);
      setInput(`تحليل ملف PDF: ${e.target.files[0].name}`);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  return (
    <Container maxWidth="md" sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        p: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            component="img"
            src="/logo.png"
            alt="MHEM AI Logo"
            sx={{ width: 40, height: 40, mr: 1 }}
          />
          <Typography variant="h6">{t('app.name')}</Typography>
        </Box>
        <Box>
          <IconButton onClick={toggleLanguage} color="inherit">
            <LanguageIcon />
          </IconButton>
          <Button 
            variant="outlined" 
            color="inherit" 
            onClick={onLogout}
            size="small"
            sx={{ ml: 1 }}
          >
            {i18n.language === 'ar' ? 'خروج' : 'Logout'}
          </Button>
        </Box>
      </Box>
      
      <Divider />
      
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column',
        gap: 2
      }}>
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              alignSelf: message.sender === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '80%'
            }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                backgroundColor: message.sender === 'user' ? 'primary.light' : 'background.paper',
                borderRadius: 2
              }}
            >
              {message.pdfData && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {i18n.language === 'ar' ? 'ملف PDF مرفق:' : 'PDF File Attached:'}
                  </Typography>
                  <Box
                    component="img"
                    src="/pdf-icon.png"
                    alt="PDF Icon"
                    sx={{ width: 40, height: 40 }}
                  />
                </Box>
              )}
              
              {message.isCode ? (
                <Box
                  sx={{
                    backgroundColor: '#1e1e1e',
                    p: 2,
                    borderRadius: 1,
                    overflowX: 'auto'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      color: '#e0e0e0',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {message.text.replace(/```[a-z]*\n|```/g, '')}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1">{message.text}</Typography>
              )}
              
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  mt: 1, 
                  textAlign: message.sender === 'user' ? 'left' : 'right',
                  opacity: 0.7
                }}
              >
                {message.timestamp.toLocaleTimeString()}
              </Typography>
            </Paper>
          </Box>
        ))}
        
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>
      
      <Paper
        elevation={3}
        sx={{
          p: 2,
          m: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <IconButton 
          color="primary" 
          onClick={() => fileInputRef.current?.click()}
        >
          <AttachFileIcon />
        </IconButton>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".pdf"
          style={{ display: 'none' }}
        />
        
        <IconButton 
          color="primary"
          onClick={() => {
            setInput(prev => prev + '\n```\n\n```');
          }}
        >
          <CodeIcon />
        </IconButton>
        
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('chat.placeholder')}
          variant="outlined"
          size="small"
        />
        
        <Button
          variant="contained"
          color="primary"
          endIcon={<SendIcon />}
          onClick={handleSend}
          disabled={isLoading || (input.trim() === '' && !pdfFile)}
        >
          {t('chat.send')}
        </Button>
      </Paper>
    </Container>
  );
};

export default Chat;
